/* eslint-disable react-hooks/set-state-in-effect */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, Gift, Sparkles, Ticket, Instagram } from 'lucide-react';
import { useLang } from '@/contexts/LanguageContext';
import { PRODUCTS } from '@/data/products';
import { BRAND } from '@/data/brand';
import { REGISTRATION } from '@/constants/testIds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Registration() {
  const { t, lang } = useLang();
  const [searchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    current_distributor: '',
    customer_type: '',
    main_interest: '',
    knew_jelly: '',
    favorite_product: '',
    would_buy: '',
    business_city: '',
    has_business_location: false,
    interested_in_distribution: false,
    buys_wholesale: false,
    enters_raffle: true,
    consent_data: false,
    source: '',
    stand_code: '',
  });

  // Capture UTM source + stand code on mount (e.g. /registro?utm=expobelleza-qr&stand=A1)
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => {
    const utm = searchParams.get('utm') || searchParams.get('utm_source') || '';
    const stand = searchParams.get('stand') || '';
    if (utm || stand) {
      setForm((f) => ({
        ...f,
        ...(utm ? { source: utm } : {}),
        ...(stand ? { stand_code: stand.toUpperCase().slice(0, 20) } : {}),
      }));
    }
  }, [searchParams]);

  const update = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/registrations`, form);
      toast.success(t('registration.success'));
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const field = typeof detail === 'object' && detail !== null ? detail.field : null;
      if (err?.response?.status === 409 && field === 'phone') {
        toast.error(t('registration.errorDuplicatePhone'));
      } else if (err?.response?.status === 409 && field === 'email') {
        toast.error(t('registration.errorDuplicateEmail'));
      } else {
        toast.error(t('registration.error'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#FFFBFB]">
      {/* HERO BANNER */}
      <section className="relative px-6 md:px-12 lg:px-24 pt-16 md:pt-24 pb-12 overflow-hidden">
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#A99BE0]/40 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full bg-[#F4DD8C]/40 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-[10px] md:text-xs font-bold tracking-[0.3em] uppercase text-[#1F1A24] bg-[#F4DD8C] px-4 py-2 rounded-full shadow-[0_8px_24px_rgba(244,221,140,0.35)]">
              <Ticket className="w-3.5 h-3.5" />
              {t('registration.raffleBadge')}
            </span>
            <h1 className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-[#1F1A24] text-balance leading-[0.95]">
              {t('registration.title')}
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#574A66] leading-relaxed max-w-xl">
              {t('registration.subtitle')}
            </p>

            <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
              <Stat icon={<Gift className="w-5 h-5 text-[#FF7597]" />} label={lang === 'es' ? 'Kits' : 'Kits'} value="x4" />
              <Stat icon={<Sparkles className="w-5 h-5 text-[#A99BE0]" />} label={lang === 'es' ? 'Sorteos' : 'Raffles'} value="4" />
              <Stat icon={<Ticket className="w-5 h-5 text-[#1F1A24]" />} label={lang === 'es' ? 'Días feria' : 'Expo days'} value="4" />
            </div>
          </div>

          <div className="relative">
            <div className="absolute -top-6 -left-6 bg-[#FF7597] text-white p-5 rounded-3xl shadow-xl rotate-[-6deg] z-10">
              <p className="text-[10px] font-bold tracking-[0.3em] uppercase">JELLY</p>
              <p className="font-brand text-3xl leading-none">KIT</p>
            </div>
            <div className="rounded-[2.5rem] overflow-hidden aspect-[4/5] bg-[#A99BE0] grain relative">
              <img
                src="https://customer-assets.emergentagent.com/job_jelly-vibes/artifacts/tgw9i22v_IMG-20260601-WA1417.jpg"
                alt="Jelly Bag prize"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="px-6 md:px-12 lg:px-24 pb-24 md:pb-32">
        <div className="max-w-3xl mx-auto rounded-[2rem] bg-white border border-[#A99BE0]/20 p-6 md:p-10 shadow-[0_24px_60px_rgba(169,155,224,0.12)]">
          {done ? (
            <div className="flex flex-col items-center text-center py-10">
              <span className="h-16 w-16 rounded-full bg-[#FAEFC2] inline-flex items-center justify-center mb-4">
                <CheckCircle2 className="w-8 h-8 text-[#A99BE0]" />
              </span>
              <p className="font-display text-3xl font-black tracking-tighter text-[#1F1A24]">{t('registration.success')}</p>
              <p className="mt-4 text-sm text-[#574A66] max-w-md">{t('registration.raffleNote')}</p>

              {/* Follow us CTA */}
              <div className="mt-10 w-full max-w-md rounded-3xl bg-gradient-to-br from-[#F4DD8C]/40 via-[#A99BE0]/15 to-[#FF7597]/20 border border-[#A99BE0]/20 p-6">
                <p className="font-display text-xl font-black tracking-tighter text-[#1F1A24]">
                  {t('registration.successFollowTitle')}
                </p>
                <p className="mt-2 text-sm text-[#574A66]">
                  {t('registration.successFollowBody')}
                </p>
                <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-center">
                  <a
                    data-testid="registration-follow-instagram"
                    href={`https://instagram.com/${BRAND.instagram}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#1F1A24] text-white text-xs font-bold tracking-widest uppercase hover:bg-[#FF7597] transition-colors"
                  >
                    <Instagram className="w-4 h-4" />
                    @{BRAND.instagram}
                  </a>
                  <a
                    data-testid="registration-follow-tiktok"
                    href={`https://www.tiktok.com/@${BRAND.tiktok}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#FF7597] text-white text-xs font-bold tracking-widest uppercase hover:bg-[#FF5C83] transition-colors"
                  >
                    <TikTokIcon className="w-4 h-4" />
                    TikTok · @{BRAND.tiktok}
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <form data-testid={REGISTRATION.form} onSubmit={submit} className="space-y-8">
              <FormSection title={t('registration.sectionContact')}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label={t('registration.name')} testId={REGISTRATION.name} value={form.name} onChange={update('name')} required />
                  <Field label={t('registration.phone')} testId={REGISTRATION.phone} value={form.phone} onChange={update('phone')} type="tel" required />
                  <Field label={t('registration.email')} testId={REGISTRATION.email} value={form.email} onChange={update('email')} type="email" required />
                  <Field label={t('registration.city')} testId={REGISTRATION.city} value={form.city} onChange={update('city')} required />
                  <SelectField
                    label={t('registration.customerType')}
                    testId={REGISTRATION.customerType}
                    value={form.customer_type}
                    onChange={update('customer_type')}
                    options={[
                      { v: '', l: t('registration.placeholder') },
                      { v: 'wholesale', l: t('registration.ct_wholesale') },
                      { v: 'retail', l: t('registration.ct_retail') },
                    ]}
                  />
                </div>
              </FormSection>

              <FormSection title={t('registration.sectionSurvey')}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <SelectField
                    label={t('registration.favoriteProduct')}
                    testId={REGISTRATION.favoriteProduct}
                    value={form.favorite_product}
                    onChange={update('favorite_product')}
                    options={[
                      { v: '', l: t('registration.favoriteProductPh') },
                      ...PRODUCTS.map((p) => ({ v: p.slug, l: p.name[lang] })),
                    ]}
                  />
                  <Field label={t('registration.businessCity')} testId={REGISTRATION.businessCity} value={form.business_city} onChange={update('business_city')} />
                </div>
              </FormSection>

              {/* Commercial intent · only visible when relevant */}
              {(form.customer_type === 'wholesale' || form.customer_type === 'distributor' || form.business_city) && (
                <FormSection title={lang === 'es' ? '¿Cómo trabajas?' : 'How do you operate?'}>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <PillCheckbox
                      testId="reg-has-local"
                      checked={form.has_business_location}
                      onChange={update('has_business_location')}
                      label={lang === 'es' ? '🏪 Tengo local comercial' : '🏪 I have a physical store'}
                    />
                    <PillCheckbox
                      testId="reg-distribution"
                      checked={form.interested_in_distribution}
                      onChange={update('interested_in_distribution')}
                      label={lang === 'es' ? '🚚 Me interesa distribuir' : '🚚 Interested in distribution'}
                    />
                    <PillCheckbox
                      testId="reg-wholesale-buyer"
                      checked={form.buys_wholesale}
                      onChange={update('buys_wholesale')}
                      label={lang === 'es' ? '📦 Compro al por mayor' : '📦 I buy wholesale'}
                    />
                  </div>
                </FormSection>
              )}

              <label className="flex items-start gap-3 cursor-pointer p-4 rounded-2xl bg-[#FAEFC2]/40 border border-[#F4DD8C]">
                <input
                  data-testid={REGISTRATION.entersRaffle}
                  type="checkbox"
                  checked={form.enters_raffle}
                  onChange={update('enters_raffle')}
                  className="mt-1 h-5 w-5 accent-[#FF7597]"
                />
                <span className="text-sm text-[#1F1A24] leading-snug">
                  <Gift className="w-4 h-4 text-[#FF7597] inline mr-1 -mt-1" />
                  {t('registration.entersRaffle')}
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer p-4 rounded-2xl bg-white border border-[#A99BE0]/30">
                <input
                  data-testid="registration-consent"
                  type="checkbox"
                  checked={form.consent_data}
                  onChange={update('consent_data')}
                  required
                  className="mt-1 h-5 w-5 accent-[#A99BE0]"
                />
                <span className="text-xs text-[#574A66] leading-snug">
                  {lang === 'es' ? (
                    <>Autorizo a Jelly by Enkor a tratar mis datos personales conforme a su{' '}
                      <Link to="/privacidad" target="_blank" className="font-bold text-[#A99BE0] underline">
                        Política de Privacidad
                      </Link>{' '}
                      (Ley 1581 Habeas Data Colombia), y a recibir información comercial por WhatsApp y correo.
                    </>
                  ) : (
                    <>I authorize Jelly by Enkor to process my personal data per its{' '}
                      <Link to="/privacidad" target="_blank" className="font-bold text-[#A99BE0] underline">
                        Privacy Policy
                      </Link>{' '}
                      and to receive commercial communications via WhatsApp and email.
                    </>
                  )}
                </span>
              </label>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  data-testid={REGISTRATION.submit}
                  type="submit"
                  disabled={submitting || !form.consent_data}
                  className="flex-1 inline-flex items-center justify-center px-7 py-4 rounded-full bg-[#FF7597] text-white text-sm font-bold tracking-widest uppercase hover:bg-[#FF5C83] hover:scale-[1.02] transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_12px_40px_rgba(255,117,151,0.35)]"
                >
                  {submitting ? t('registration.submitting') : t('registration.submit')}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

const FormSection = ({ title, children }) => (
  <div>
    <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#A99BE0] mb-4">{title}</p>
    {children}
  </div>
);

const Label = ({ children }) => (
  <label className="block text-[10px] font-bold tracking-[0.25em] uppercase text-[#574A66] mb-2">{children}</label>
);

const Field = ({ label, testId, value, onChange, type = 'text', required }) => (
  <div>
    <Label>{label}</Label>
    <input
      data-testid={testId}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full rounded-2xl border border-[#A99BE0]/30 bg-[#FFFBFB] px-4 py-3 text-sm focus:outline-none focus:border-[#A99BE0] focus:ring-2 focus:ring-[#A99BE0]/20 transition-all"
    />
  </div>
);

const SelectField = ({ label, testId, value, onChange, options }) => (
  <div>
    <Label>{label}</Label>
    <select
      data-testid={testId}
      value={value}
      onChange={onChange}
      required
      className="w-full rounded-2xl border border-[#A99BE0]/30 bg-[#FFFBFB] px-4 py-3 text-sm focus:outline-none focus:border-[#A99BE0] focus:ring-2 focus:ring-[#A99BE0]/20 transition-all"
    >
      {options.map((o) => (
        <option key={o.v} value={o.v} disabled={o.v === ''}>{o.l}</option>
      ))}
    </select>
  </div>
);

const PillCheckbox = ({ testId, checked, onChange, label }) => (
  <label
    className={`flex items-center gap-3 cursor-pointer p-3 rounded-2xl border-2 transition-all select-none ${
      checked
        ? 'bg-[#FFE6EB] border-[#FF7597] shadow-[0_6px_18px_rgba(255,117,151,0.18)]'
        : 'bg-white border-[#A99BE0]/30 hover:border-[#A99BE0]'
    }`}
  >
    <input
      data-testid={testId}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="h-5 w-5 accent-[#FF7597]"
    />
    <span className="text-sm font-semibold text-[#1F1A24] leading-snug">{label}</span>
  </label>
);

const Stat = ({ icon, label, value }) => (
  <div className="rounded-2xl bg-white border border-[#A99BE0]/20 p-3 text-center shadow-sm">
    <div className="flex justify-center">{icon}</div>
    <p className="mt-2 font-display font-black text-2xl text-[#1F1A24] tracking-tighter">{value}</p>
    <p className="text-[10px] text-[#574A66] tracking-wider uppercase">{label}</p>
  </div>
);

const TikTokIcon = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M16.5 3a5.5 5.5 0 0 0 4.5 4.5v3a8.5 8.5 0 0 1-4.5-1.3v6.6a6.2 6.2 0 1 1-6.2-6.2c.34 0 .67.03 1 .08v3.1a3.1 3.1 0 1 0 2.2 3v-12.5h3z"/>
  </svg>
);
